import { useEffect, useState } from 'react'
import Icon from '../components/Icon'
import Toggle from '../components/Toggle'
import ConfirmDialog from '../components/ConfirmDialog'
import { fetchSkillSettings, saveSkillSettings } from '../api/admin'
import { DEFAULT_SKILL_SETTINGS } from './adminMockData'
import styles from './AdminSkillsTab.module.css'

const MIN_TICKETS = 1
const MAX_TICKETS = 50

function temperatureLabel(v) {
  if (v < 0.4) return 'Conservative'
  if (v < 0.7) return 'Balanced'
  return 'Exploratory'
}

function thresholdLabel(v) {
  if (v < 40) return 'Permissive'
  if (v < 85) return 'Standard'
  return 'Strict'
}

/**
 * AdminSkillsTab — Skills Settings: sensitivity sliders, auto-approve
 * toggles, and the ticket-assignment cap. Backed by gaussiq-admin's
 * /admin/settings/skills (tenant_props under the hood) — see src/api/admin.js.
 */
export default function AdminSkillsTab() {
  const [saved, setSaved] = useState(DEFAULT_SKILL_SETTINGS)
  const [draft, setDraft] = useState(DEFAULT_SKILL_SETTINGS)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [confirmingSave, setConfirmingSave] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetchSkillSettings()
      .then(settings => {
        if (cancelled) return
        setSaved(settings)
        setDraft(settings)
      })
      .catch(err => { if (!cancelled) setError(err.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [])

  const dirty = JSON.stringify(saved) !== JSON.stringify(draft)

  function set(key, value) {
    setDraft(prev => ({ ...prev, [key]: value }))
  }

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const settings = await saveSkillSettings(draft)
      setSaved(settings)
      setDraft(settings)
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  function handleSaveClick() {
    setConfirmingSave(true)
  }

  function handleReset() {
    setDraft(saved)
  }

  function step(delta) {
    set('maxTicketAssignment', Math.min(MAX_TICKETS, Math.max(MIN_TICKETS, draft.maxTicketAssignment + delta)))
  }

  if (loading) {
    return <div className={styles.wrap}><div className={`${styles.panel} ${styles.loadingText}`}>Loading…</div></div>
  }

  return (
    <div className={styles.wrap}>
      {error && (
        <div className={`${styles.panel} ${styles.errorBanner}`} role="alert">
          <span>{error}</span>
          <button
            type="button"
            className={styles.errorDismiss}
            onClick={() => setError(null)}
            aria-label="Dismiss"
          >
            <Icon name="close" />
          </button>
        </div>
      )}
      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <h2 className={styles.panelTitle}>Sensitivity &amp; thresholds</h2>
          <p className={styles.panelSub}>Tune how readily Gaussiq proposes and accepts skills across the workspace.</p>
        </div>

        {/* ── Organization skill temperature ────────── */}
        <div className={styles.row}>
          <div className={styles.rowTop}>
            <div className={styles.rowCopy}>
              <span className={styles.rowLabel}>Organization skill temperature</span>
              <p className={styles.rowDescription}>
                How exploratory Gaussiq is when generating skills for the whole organisation. Lower
                keeps output conservative and close to proven patterns; higher allows more novel proposals.
              </p>
            </div>
            <div className={styles.rowValue}>
              <span className={styles.valueNumber}>{draft.orgSkillTemperature.toFixed(2)}</span>
              <span className={styles.valueTag}>{temperatureLabel(draft.orgSkillTemperature).toUpperCase()}</span>
            </div>
          </div>
          <input
            type="range"
            className={styles.slider}
            min={0}
            max={1}
            step={0.01}
            value={draft.orgSkillTemperature}
            onChange={e => set('orgSkillTemperature', Number(e.target.value))}
            style={{ '--pct': `${draft.orgSkillTemperature * 100}%` }}
          />
          <div className={styles.sliderLabels}>
            <span>0.00 · Conservative</span>
            <span>Exploratory · 1.00</span>
          </div>
        </div>

        {/* ── Individual skill evidence threshold ───── */}
        <div className={styles.row}>
          <div className={styles.rowTop}>
            <div className={styles.rowCopy}>
              <span className={styles.rowLabel}>Individual skill evidence threshold</span>
              <p className={styles.rowDescription}>
                Minimum evidence strength a skill must reach before it can be submitted for approval.
                Raising it reduces false positives but slows the pipeline.
              </p>
            </div>
            <div className={styles.rowValue}>
              <span className={styles.valueNumber}>{draft.individualEvidenceThreshold}%</span>
              <span className={styles.valueTag}>{thresholdLabel(draft.individualEvidenceThreshold).toUpperCase()}</span>
            </div>
          </div>
          <input
            type="range"
            className={styles.slider}
            min={0}
            max={100}
            step={1}
            value={draft.individualEvidenceThreshold}
            onChange={e => set('individualEvidenceThreshold', Number(e.target.value))}
            style={{ '--pct': `${draft.individualEvidenceThreshold}%` }}
          />
          <div className={styles.sliderLabels}>
            <span>0% · Permissive</span>
            <span>Strict · 100%</span>
          </div>
        </div>

        {/* ── Auto-approve toggles ───────────────────── */}
        <div className={styles.row}>
          <Toggle
            label="Auto-approve Org Skills"
            description="Publish organisation-wide skills without a reviewer once they clear the evidence threshold."
            tone="blue"
            checked={draft.autoApproveOrgSkills}
            onChange={v => set('autoApproveOrgSkills', v)}
          />
        </div>

        <div className={styles.row}>
          <Toggle
            label="Auto-approve Individual Skills"
            description="Publish a user's own skills automatically, subject to their team's approval rights."
            tone="purple"
            checked={draft.autoApproveIndividualSkills}
            onChange={v => set('autoApproveIndividualSkills', v)}
          />
        </div>

        {/* ── Max ticket assignment ──────────────────── */}
        <div className={styles.row}>
          <div className={styles.rowTop}>
            <div className={styles.rowCopy}>
              <span className={styles.rowLabel}>Max ticket assignment</span>
              <p className={styles.rowDescription}>
                The cut-off for assigning tickets to an agent. Once an agent holds this many open tickets,
                Gaussiq stops routing new work to them.
              </p>
            </div>
            <div className={styles.stepper}>
              <button
                type="button"
                className={styles.stepperBtn}
                onClick={() => step(-1)}
                disabled={draft.maxTicketAssignment <= MIN_TICKETS}
                aria-label="Decrease"
              >
                −
              </button>
              <div className={styles.stepperValue}>
                <span className={styles.stepperNumber}>{draft.maxTicketAssignment}</span>
                <span className={styles.stepperUnit}>Tickets</span>
              </div>
              <button
                type="button"
                className={styles.stepperBtn}
                onClick={() => step(1)}
                disabled={draft.maxTicketAssignment >= MAX_TICKETS}
                aria-label="Increase"
              >
                +
              </button>
            </div>
          </div>
        </div>

        {/* ── Footer ──────────────────────────────────── */}
        <div className={styles.footer}>
          <span className={styles.footerStatus}>{saving ? 'Saving…' : dirty ? 'Unsaved changes' : 'All changes saved'}</span>
          <div className={styles.footerActions}>
            <button type="button" className={styles.resetBtn} onClick={handleReset} disabled={!dirty || saving}>
              Discard changes
            </button>
            <button type="button" className={styles.saveBtn} onClick={handleSaveClick} disabled={!dirty || saving}>
              Save changes
            </button>
          </div>
        </div>
      </div>

      {confirmingSave && (
        <ConfirmDialog
          title="Save skill settings"
          message="These settings affect skill generation and approval across the entire workspace. Save changes?"
          confirmLabel="Save changes"
          onConfirm={() => { setConfirmingSave(false); handleSave() }}
          onCancel={() => setConfirmingSave(false)}
        />
      )}
    </div>
  )
}
